import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getProceduresServices } from "../../services/api";
import { ProcedureService } from "../../types/types";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import { getFileName, resolveMediaUrl } from "../../utils/media";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTasks,
  faCalendarAlt,
  faArrowRight,
  faSpinner,
  faFilePdf,
  faImage,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";

const Procedures: React.FC = () => {
  const [procedures, setProcedures] = useState<ProcedureService[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);
  const { language } = useLanguage();

  useEffect(() => {
    fetchProcedures();
  }, []);

  const fetchProcedures = async () => {
    try {
      setLoading(true);
      const response = await getProceduresServices();
      setProcedures(response.data);
    } catch (error) {
      console.error("Error fetching procedures:", error);
    } finally {
      setLoading(false);
    }
  };

  // Strip HTML tags from text
  const stripHtmlTags = (html: string): string => {
    if (!html) return "";
    return html.replace(/<\/?[^>]+(>|$)/g, "");
  };

  // Truncate text with ellipsis
  const truncateText = (text: string, maxLength: number): string => {
    const strippedText = stripHtmlTags(text);
    if (strippedText.length <= maxLength) return strippedText;
    return strippedText.substring(0, maxLength) + "...";
  };

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = procedures.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(procedures.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  // Generate page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  // Get appropriate title based on language
  const getTitle = (titrearabe?: string, titrefrancais?: string) => {
    return language === "ar"
      ? titrearabe || titrefrancais || "بدون عنوان"
      : titrefrancais || titrearabe || "Sans titre";
  };

  // Get description without HTML tags
  const getDescription = (textarabe?: string, textfrancais?: string) => {
    const text = language === "ar" ? textarabe : textfrancais;
    return truncateText(text || "", 150);
  };

  // Direction and text alignment
  const getDirectionClass = () => (language === "ar" ? "rtl" : "ltr");
  const getTextAlignClass = () =>
    language === "ar" ? "text-right" : "text-left";

  return (
    <div className={getDirectionClass()}>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-mauritania-green-dark to-mauritania-green text-white py-20 text-center">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">
            {getTranslation("proceduresServices", language) ||
              (language === "ar"
                ? "الإجراءات والخدمات"
                : "Procédures et Services")}
          </h1>
          <p className="text-xl">
            {language === "ar"
              ? "جميع الإجراءات والخدمات المقدمة من الوزارة للجمهور"
              : "Toutes les procédures et services offerts par le ministère au public"}
          </p>
        </div>
      </section>

      {/* Procedures Section */}
      <section className="py-16 px-8 bg-gray-50" id="procedures">
        <div className="container mx-auto">
          <div className="text-center mb-12 relative">
            <div className="flex items-center justify-center mb-4">
              <FontAwesomeIcon
                icon={faTasks}
                className="text-mauritania-gold text-4xl mr-3"
              />
              <h2 className="text-3xl font-bold text-mauritania-green-dark inline-block pb-2">
                {getTranslation("proceduresServices", language) ||
                  (language === "ar"
                    ? "الإجراءات والخدمات"
                    : "Procédures et Services")}
              </h2>
            </div>
            <div className="absolute bottom-0 right-1/2 transform translate-x-1/2 w-20 h-1 bg-mauritania-gold"></div>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <FontAwesomeIcon
                icon={faSpinner}
                className="text-mauritania-green-dark text-4xl animate-spin mb-4"
              />
              <p className="text-gray-600">
                {getTranslation("loading", language)}
              </p>
            </div>
          ) : procedures.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                {currentItems.map((procedure) => (
                  <div
                    key={procedure.id}
                    className="bg-white text-mauritania-green-dark rounded-lg overflow-hidden shadow-custom hover:shadow-lg transition duration-300 hover:-translate-y-1 flex flex-col h-full"
                  >
                    {/* Image Section */}
                    {procedure.image && (
                      <div className="h-48 overflow-hidden">
                        <img
                          src={resolveMediaUrl(procedure.image)}
                          alt={getTitle(
                            procedure.titrearabe,
                            procedure.titrefrancais
                          )}
                          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}

                    {/* Content Section */}
                    <div className="p-6 flex flex-col flex-grow">
                      <h3 className="text-xl font-bold mb-4 line-clamp-2">
                        {getTitle(
                          procedure.titrearabe,
                          procedure.titrefrancais
                        )}
                      </h3>

                      <div className="text-gray-600 mb-6 line-clamp-3 flex-grow">
                        {getDescription(
                          procedure.textarabe,
                          procedure.textfrancais
                        )}
                      </div>

                      {/* File Download Section */}
                      {procedure.fichier && (
                        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <FontAwesomeIcon
                                icon={faFilePdf}
                                className="text-red-500 mr-2"
                              />
                              <span className="text-sm font-medium">
                                {language === "ar"
                                  ? "ملف مرفق"
                                  : "Fichier joint"}
                              </span>
                            </div>
                            <a
                              href={resolveMediaUrl(procedure.fichier)}
                              download={getFileName(procedure.fichier)}
                              className="text-mauritania-green hover:text-mauritania-green-dark text-sm"
                              title={
                                language === "ar"
                                  ? "تحميل الملف"
                                  : "Télécharger le fichier"
                              }
                            >
                              <FontAwesomeIcon icon={faDownload} />
                            </a>
                          </div>
                        </div>
                      )}

                      {/* View Details Button */}
                      <div className="flex justify-between items-center mt-auto">
                        <Link
                          to={`/procedures/${procedure.id}`}
                          className="inline-flex items-center text-mauritania-green font-semibold hover:text-mauritania-green-dark transition duration-300"
                        >
                          {getTranslation("viewDetails", language) ||
                            (language === "ar"
                              ? "عرض التفاصيل"
                              : "Voir les détails")}
                          <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`ml-2 ${
                              language === "ar" ? "rotate-180" : ""
                            }`}
                          />
                        </Link>

                        {/* File Type Indicator */}
                        {procedure.fichier && (
                          <span className="text-xs px-2 py-1 bg-mauritania-green-light text-mauritania-green-dark rounded">
                            PDF
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-8">
                  <nav className="flex items-center space-x-2">
                    {/* Previous button */}
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === 1
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-mauritania-green text-white hover:bg-mauritania-green-dark"
                      }`}
                    >
                      {language === "ar" ? "السابق" : "Précédent"}
                    </button>

                    {/* Page numbers */}
                    {pageNumbers.map((number) => (
                      <button
                        key={number}
                        onClick={() => paginate(number)}
                        className={`px-3 py-1 rounded-md ${
                          currentPage === number
                            ? "bg-mauritania-gold text-mauritania-green-dark font-bold"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      >
                        {number}
                      </button>
                    ))}

                    {/* Next button */}
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 rounded-md ${
                        currentPage === totalPages
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-mauritania-green text-white hover:bg-mauritania-green-dark"
                      }`}
                    >
                      {language === "ar" ? "التالي" : "Suivant"}
                    </button>
                  </nav>
                </div>
              )}

              {/* Show current page info */}
              {procedures.length > 0 && (
                <div
                  className={`text-center mt-4 text-gray-600 ${getTextAlignClass()}`}
                >
                  {language === "ar"
                    ? `عرض ${indexOfFirstItem + 1} - ${Math.min(
                        indexOfLastItem,
                        procedures.length
                      )} من أصل ${procedures.length} إجراء`
                    : `Affichage ${indexOfFirstItem + 1} - ${Math.min(
                        indexOfLastItem,
                        procedures.length
                      )} sur ${procedures.length} procédures`}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <div className="bg-white p-8 rounded-lg shadow-md">
                <svg
                  className="w-16 h-16 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  {language === "ar"
                    ? "لا توجد إجراءات متاحة"
                    : "Aucune procédure disponible"}
                </h3>
                <p className="text-gray-500">
                  {language === "ar"
                    ? "لم يتم إضافة أي إجراءات أو خدمات حتى الآن. يرجى التحقق لاحقاً."
                    : "Aucune procédure ou service n'a été ajouté pour le moment. Veuillez vérifier plus tard."}
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Procedures;
